import React, { useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Tooltip, CircularProgress } from '@mui/material';
import {
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  ViewColumn,
} from '@mui/icons-material';
import { debounce } from 'lodash';
import ColumnFilter from './FilterComponents/ColumnFilter';
import useColumnFilters from './hooks/useColumnFilters';

const DataTableFilters = ({
  columnFilters = [],
  filtering,
  onFilter,
  onReset,
  onRefresh,
  enableColumnPicker,
  onColumnPickerOpen,
  loading = false,
}) => {
  const [localFilters, setLocalFilters] = useState({});
  const { getFilterOptions, isFilterLoading } = useColumnFilters(columnFilters);

  const debouncedHandlers = {};
  columnFilters.forEach((filter) => {
    if (filter.debounce) {
      debouncedHandlers[filter.key] = useCallback(
        debounce((value) => {
          onFilter(filter.key, value);
        }, filter.debounceDelay || 500),
        [onFilter],
      );
    }
  });

  useEffect(() => {
    const initialLocal = {};
    columnFilters.forEach((filter) => {
      initialLocal[filter.key] = filtering[filter.key] || '';
    });
    setLocalFilters(initialLocal);
  }, [filtering, columnFilters]);

  const handleFilterChange = useCallback(
    (key, value) => {
      setLocalFilters((prev) => ({ ...prev, [key]: value }));

      const filterConfig = columnFilters.find((f) => f.key === key);

      if (filterConfig?.debounce && debouncedHandlers[key]) {
        debouncedHandlers[key](value);
      } else {
        onFilter(key, value);
      }
    },
    [columnFilters, debouncedHandlers, onFilter],
  );

  const handleResetFilters = () => {
    const resetFilters = {};
    columnFilters.forEach((filter) => {
      resetFilters[filter.key] = '';
    });
    setLocalFilters(resetFilters);
    onReset();
  };

  const getActiveFiltersCount = () => {
    return Object.values(localFilters).filter(
      (value) => value !== null && value !== undefined && value !== '',
    ).length;
  };

  return (
    <Box
      sx={{
        bgcolor: 'grey.50',
        border: 1,
        borderColor: 'grey.200',
        borderRadius: 1,
        p: 1.5,
        mb: 2,
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
        }}>
        {enableColumnPicker && (
          <Tooltip title='Show/hide columns'>
            <IconButton
              size='small'
              onClick={onColumnPickerOpen}
              sx={{ p: 0.5 }}>
              <ViewColumn fontSize='small' />
            </IconButton>
          </Tooltip>
        )}

        {columnFilters.map((filterConfig) => (
          <ColumnFilter
            key={filterConfig.key}
            filterConfig={filterConfig}
            value={localFilters[filterConfig.key]}
            onChange={handleFilterChange}
            options={getFilterOptions(filterConfig.key)}
            loading={isFilterLoading(filterConfig.key)}
          />
        ))}

        <Tooltip title='Clear filters'>
          <IconButton
            size='small'
            onClick={handleResetFilters}
            disabled={getActiveFiltersCount() === 0}
            sx={{ flex: '0 0 auto', p: 0.5 }}>
            <ClearIcon fontSize='small' />
          </IconButton>
        </Tooltip>

        <Tooltip title='Refresh data'>
          <IconButton
            size='small'
            onClick={onRefresh}
            sx={{ flex: '0 0 auto', p: 0.5 }}>
            <RefreshIcon fontSize='small' />
          </IconButton>
        </Tooltip>

        {loading && (
          <CircularProgress
            size={16}
            sx={{
              ml: 1,
              color: 'text.secondary',
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default DataTableFilters;
